import fromUnixTime from 'date-fns/fromUnixTime';
import type { MerkleDropSchedule, RangeEditionSchedule } from '@soundxyz/sdk/types';
import type { Maybe } from '@soundxyz/utils';
import { MintingTypes } from '@soundxyz/utils/const';
import { isOpenEdition } from './shared-helpers';

export const getMerkleRoot = (
  schedule:
    | Pick<RangeEditionSchedule, 'mintType'>
    | Pick<MerkleDropSchedule, 'merkleRoot' | 'mintType'>,
): Maybe<string> => {
  if (schedule.mintType === 'MerkleDrop') return schedule.merkleRoot;

  return null;
};

export type MintTypeScheduleInfer =
  | Pick<MerkleDropSchedule, 'mintType' | 'price' | 'maxMintable' | 'endTime' | 'startTime'>
  | Pick<
      RangeEditionSchedule,
      | 'mintType'
      | 'price'
      | 'maxMintableLower'
      | 'maxMintableUpper'
      | 'endTime'
      | 'cutoffTime'
      | 'startTime'
    >;

export const toMintTypeFromSchedule = (schedule: MintTypeScheduleInfer): MintingTypes => {
  const isFree = schedule.mintType === 'MerkleDrop' && schedule.price.toString() === '0';
  if (isFree) return MintingTypes['FREE'];
  const isPresale = schedule.mintType === 'MerkleDrop' && schedule.price.toString() !== '0';
  if (isPresale) return MintingTypes['PRESALE'];
  const isFixed =
    schedule.mintType === 'RangeEdition' && schedule.maxMintableLower === schedule.maxMintableUpper;
  if (isFixed) return MintingTypes['FIXED'];
  const isRanged =
    schedule.mintType === 'RangeEdition' && schedule.maxMintableLower < schedule.maxMintableUpper;
  const isThisOpen = isRanged && isOpenEdition(schedule.maxMintableUpper);
  if (isThisOpen) return MintingTypes['OPEN_EDITION'];
  if (isRanged) return MintingTypes['RANGE'];
  throw Error(`Could not get mint type for schedule ${schedule.mintType}`);
};

export const isPublicSale = (type: MintingTypes): boolean => {
  switch (type) {
    case MintingTypes.FIXED:
    case MintingTypes.RANGE:
    case MintingTypes.OPEN_EDITION:
      return true;
    default:
      return false;
  }
};

export const getMinSupply = (
  schedule:
    | Pick<MerkleDropSchedule, 'mintType' | 'maxMintable'>
    | Pick<RangeEditionSchedule, 'mintType' | 'maxMintableLower'>,
): number => {
  const type = schedule.mintType;

  switch (type) {
    case 'MerkleDrop':
      return schedule.maxMintable;
    case 'RangeEdition':
      return schedule.maxMintableLower;
    default:
      throw Error(`Could not get min supply for schedule ${type}`);
  }
};

export const getMaxSupply = (
  schedule:
    | Pick<MerkleDropSchedule, 'maxMintable' | 'mintType'>
    | Pick<RangeEditionSchedule, 'maxMintableLower' | 'maxMintableUpper' | 'mintType'>,
): number => {
  const type = schedule.mintType;

  switch (type) {
    case 'MerkleDrop':
      return schedule.maxMintable;
    case 'RangeEdition':
      return schedule.maxMintableUpper;
    default:
      throw Error(`Could not get max supply for schedule ${type}`);
  }
};

export const getMintExpiry = (schedule: MintTypeScheduleInfer): number => {
  const type = schedule.mintType;

  switch (type) {
    case 'MerkleDrop':
      return schedule.endTime;
    case 'RangeEdition':
      const mintType = toMintTypeFromSchedule(schedule);
      // On ranged sale we end the sale on the `cutOffTime`
      if (mintType === 'Range' || mintType === 'Open Edition') return schedule.cutoffTime;
      return schedule.endTime;
    default:
      throw Error(`Could not get mint expiry for schedule ${type}`);
  }
};

export const hasSaleEnded = ({
  schedule,
  collected,
}: {
  schedule: MintTypeScheduleInfer;
  collected: number;
}) => {
  const type = toMintTypeFromSchedule(schedule);
  const minSupply = getMinSupply(schedule);
  const maxSupply = getMaxSupply(schedule);
  const mintExpiryMs = getMintExpiry(schedule);

  const isFixed = type === MintingTypes.FIXED;
  // Open edition is a special case of range
  const isRange = type === MintingTypes.RANGE || type === MintingTypes.OPEN_EDITION;

  const mintExpiryTime = mintExpiryMs ? fromUnixTime(mintExpiryMs) : 0;

  const now = new Date();
  const timeExpired = now > mintExpiryTime;

  const saleEnded = (() => {
    if (isFixed) return collected === maxSupply;
    if (isRange && timeExpired) return collected >= minSupply;
    if (timeExpired) return timeExpired;
    return collected === maxSupply;
  })();

  return saleEnded;
};

export const isScheduleSoldOut = ({
  schedule,
  collected,
}: {
  schedule: MintTypeScheduleInfer;
  collected: number;
}) => {
  const type = toMintTypeFromSchedule(schedule);
  const minSupply = getMinSupply(schedule);
  const maxSupply = getMaxSupply(schedule);

  // Open edition is a special case of range
  const isRange = type === MintingTypes.RANGE || type === MintingTypes.OPEN_EDITION;

  const saleEnded = hasSaleEnded({ schedule, collected });

  if (isRange && saleEnded) return true;
  if (saleEnded) return collected >= minSupply;
  // For normal auction mechanism the sale is considered sold out when the supply is met.
  // When SAM is enabled it will go beyond the max supply of the minters, since SAM has it's own max supply.
  // So it is a safe assumption to say that if the sale has ended and the collected is greater than the max supply
  return collected >= maxSupply;
};

export const getSupply = ({
  schedule,
  collected,
}: {
  schedule: MintTypeScheduleInfer;
  collected: number;
}) => {
  const type = toMintTypeFromSchedule(schedule);
  const minSupply = getMinSupply(schedule);
  const mintExpiryMs = getMintExpiry(schedule);

  const isRange = type === MintingTypes.RANGE || type === MintingTypes.OPEN_EDITION;

  const mintExpiryTime = mintExpiryMs ? fromUnixTime(mintExpiryMs) : 0;

  const now = new Date();
  const timeExpired = now > mintExpiryTime;

  const saleEnded = hasSaleEnded({ schedule, collected });

  const supply = (() => {
    const maxSupply = getMaxSupply(schedule);
    if (isRange && timeExpired) {
      if (saleEnded) return collected;
      return minSupply;
    }
    return maxSupply;
  })();

  return supply;
};

export function getScheduleId(schedule: { mintId: bigint; minterAddress: string }) {
  return `${schedule.mintId}-${schedule.minterAddress}`;
}

export const isPublicSaleSoldOut = ({
  publicSaleSchedule,
  finalQuantity,
  rootTotalMinted,
}: {
  publicSaleSchedule: MintTypeScheduleInfer;
  finalQuantity: number | null;
  rootTotalMinted: number;
}) => {
  const totalMinted = rootTotalMinted;

  /**
   * If the final quantity is set, we use that to determine if the sale is sold out.
   * we check for null because the final quantity can be 0 (unlikely but possible)
   */
  if (finalQuantity !== null) {
    return totalMinted >= finalQuantity;
  }

  return isScheduleSoldOut({
    schedule: publicSaleSchedule,
    collected: totalMinted,
  });
};
